import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, formatCDF } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import MobileHeader from "../../components/MobileHeader";
import BottomNav from "../../components/BottomNav";
import { Badge, Card, StatusBadge } from "../../components/ui-kit";

export default function DriverWallet() {
  const { user, profile } = useAuth();
  const workerRole = user?.role === "courier" ? "courier" : "driver";
  const [payoutData, setPayoutData] = useState(null);
  const [rides, setRides] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/driver/payouts"),
      api.get("/rides"),
    ])
      .then(([payoutRes, rideRes]) => {
        setPayoutData(payoutRes.data);
        setRides((rideRes.data || []).filter((ride) => !ride.driver_id || ride.driver_id === user?.id));
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, [user?.id]);

  const earnings = payoutData?.earnings_total ?? profile?.driver_profile?.earnings_total ?? 0;
  const available = payoutData?.earnings_available ?? profile?.driver_profile?.earnings_available ?? 0;
  const totalRides = profile?.driver_profile?.total_rides || rides.length || 0;
  const paidRides = rides.filter((ride) => ride.payment_status === "COMPLETED");

  return (
    <div className="phone-frame phone-scroll-safe bg-premium-grad">
      <MobileHeader title="Revenus" />
      <div className="space-y-4 px-4 pt-2">
        <Card className="overflow-hidden p-0">
          <div className="bg-gradient-to-br from-secondary via-secondary to-primary px-5 py-6 text-secondary-foreground">
            <div className="text-[10px] font-bold uppercase tracking-widest opacity-80">Revenus enregistres</div>
            <div className="mt-1 font-heading text-4xl font-bold" data-testid="driver-earnings">{formatCDF(earnings)}</div>
            <div className="mt-1 text-[11px] opacity-80">Politique: 20% plateforme, 80% chauffeur.</div>
          </div>
        </Card>

        <Card className="border-secondary/20 bg-secondary/10 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-heading text-lg font-bold">Paiements directs</div>
              <div className="mt-1 text-xs leading-5 text-muted-foreground">
                Le wallet interne n'est pas encore disponible. Le client paie directement en cash, M-Pesa, Airtel Money ou Orange Money, puis vous confirmez la reception dans l'application.
              </div>
            </div>
            <Badge tone="warning">Historique seul</Badge>
          </div>
        </Card>

        <div className="grid grid-cols-2 gap-3">
          <Card className="p-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Courses</div>
            <div className="font-heading text-2xl font-bold">{totalRides}</div>
          </Card>
          <Card className="p-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Courses payees</div>
            <div className="font-heading text-2xl font-bold">{paidRides.length}</div>
          </Card>
          <Card className="p-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">A recevoir plus tard</div>
            <div className="font-heading text-2xl font-bold">{formatCDF(available)}</div>
          </Card>
          <Card className="p-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Retraits internes</div>
            <div className="font-heading text-lg font-bold text-muted-foreground">Indisponible</div>
          </Card>
        </div>

        <Card className="p-0">
          <div className="border-b border-white/10 p-4">
            <div className="font-heading text-lg font-bold">Historique de paiement</div>
            <div className="text-xs text-muted-foreground">Courses et confirmations recues.</div>
          </div>
          <div className="divide-y divide-white/5">
            {rides.length === 0 && <div className="p-6 text-center text-xs text-muted-foreground">Aucun paiement</div>}
            {rides.map((ride) => (
              <div key={ride.id} className="p-3" data-testid={`driver-payment-history-${ride.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-xs font-bold">Course #{ride.id.slice(0, 8).toUpperCase()}</div>
                    <div className="mt-1 text-[10px] text-muted-foreground">
                      {new Date(ride.created_at).toLocaleString("fr-FR")} · {ride.payment_method || "cash"}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="font-mono text-sm font-bold">{formatCDF(ride.price || 0)}</div>
                    <StatusBadge status={ride.payment_status || "PENDING"} />
                  </div>
                </div>
                {ride.payment_reference && (
                  <div className="mt-2 rounded-xl bg-white/5 px-3 py-2 text-[11px] text-muted-foreground">
                    Reference: {ride.payment_reference}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>

        {payoutData?.payouts?.length > 0 && (
          <Card className="p-0">
            <div className="border-b border-white/10 p-4 font-heading text-lg font-bold">Anciennes demandes de retrait</div>
            <div className="divide-y divide-white/5">
              {payoutData.payouts.map((p) => (
                <div key={p.id} className="p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-mono text-sm font-bold">{formatCDF(p.amount)}</div>
                    <StatusBadge status={p.status} />
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{p.method} - {p.account_name} - {p.account_number}</div>
                  {p.proof_reference && <div className="mt-1 text-[11px] text-secondary">Preuve: {p.proof_reference}</div>}
                  {p.admin_note && <div className="mt-1 text-[11px] text-muted-foreground">Note admin: {p.admin_note}</div>}
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
      <BottomNav role={workerRole} />
    </div>
  );
}
